import { ref } from 'vue';
import type { PermissionCategory } from '@/entities/Permission';
import { sortCategories } from '@/entities/Permission';

export type PermissionsSearchCriterion = 'permissions' | 'positions';

export interface PermissionsFilterResult {
  renderedCategories: PermissionCategory[];
  hiddenCategoryIds: number[];
  emptySectionCategoryIds: number[];
  hasGlobalNoResults: boolean;
}

const normalizeSearchValue = (value: string) => value.trim().toLowerCase();

export function usePermissions() {
  const originalCategoriesOrder = ref<Map<number, PermissionCategory>>(new Map());
  const searchTerm = ref('');
  const searchCriterion = ref<PermissionsSearchCriterion>('permissions');

  const handleSearch = (term: string) => {
    searchTerm.value = term;
  };

  const handleSearchCriterionChange = (criterion: PermissionsSearchCriterion) => {
    searchCriterion.value = criterion;
  };

  const handleCategorySort = (
    categories: PermissionCategory[],
    categoryId: number,
    sort: string,
    order: string
  ): PermissionCategory[] => {
    return sortCategories(categories, categoryId, sort, order, originalCategoriesOrder.value);
  };

  const filterCategories = (categories: PermissionCategory[]): PermissionsFilterResult => {
    const normalizedTerm = normalizeSearchValue(searchTerm.value);

    if (normalizedTerm === '') {
      return {
        renderedCategories: categories,
        hiddenCategoryIds: [],
        emptySectionCategoryIds: [],
        hasGlobalNoResults: false,
      };
    }

    if (searchCriterion.value === 'permissions') {
      const renderedCategories = categories.filter((category) =>
        category.permissions.some((permission) =>
          permission.description.toLowerCase().includes(normalizedTerm)
        )
      );

      const hiddenCategoryIds = categories
        .filter(
          (category) => !renderedCategories.some((visibleCategory) => visibleCategory.id === category.id)
        )
        .map((category) => category.id);

      return {
        renderedCategories,
        hiddenCategoryIds,
        emptySectionCategoryIds: [],
        hasGlobalNoResults: renderedCategories.length === 0 && categories.length > 0,
      };
    }

    const renderedCategories = categories.map((category) => ({
      ...category,
      positions: category.positions.filter((position) =>
        position.name.toLowerCase().includes(normalizedTerm)
      ),
    }));

    const emptySectionCategoryIds = renderedCategories
      .filter((category) => category.positions.length === 0)
      .map((category) => category.id);

    return {
      renderedCategories,
      hiddenCategoryIds: [],
      emptySectionCategoryIds,
      hasGlobalNoResults:
        renderedCategories.length > 0 &&
        renderedCategories.every((category) => category.positions.length === 0),
    };
  };

  const filteredCategories = (
    categories: PermissionCategory[],
    selectedCategoryFilter: number | null
  ): PermissionsFilterResult => {
    const baseCategories =
      selectedCategoryFilter === null
        ? categories
        : categories.filter((category) => category.id === selectedCategoryFilter);

    return filterCategories(baseCategories);
  };

  return {
    searchTerm,
    searchCriterion,
    handleSearch,
    handleSearchCriterionChange,
    handleCategorySort,
    filteredCategories,
  };
}
